import { format } from "date-fns";
import { OverlayEvent } from "./overlay.types";

export type DailyHours = {
    bookedHours: number;
    scheduledHours: number;
};

export type DailyHoursMap = Record<string, DailyHours>;

const MINUTES_PER_DAY = 24 * 60;
const BREAK_HOURS_WEEKDAY = 0.75;
const BREAK_HOURS_SATURDAY = 0.5;

const toDateKey = (date: Date) => {
    const key = format(date, "yyyy-MM-dd");
    return key;
};

const breakHoursForDate = (date: Date) => {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0) return 0;
    if (dayOfWeek === 6) return BREAK_HOURS_SATURDAY;
    return BREAK_HOURS_WEEKDAY;
};

export type DateRangeBounds = {
    start: Date;
    end: Date;
};

const isWithinRange = (date: Date, range: DateRangeBounds | undefined) => {
    if (!range) return true;
    const time = date.getTime();
    const inRange = time >= range.start.getTime() && time <= range.end.getTime();
    return inRange;
};

type DayConcurrency = {
    bookedPerMinute: Uint16Array;
    scheduledPerMinute: Uint16Array;
    technicians: Set<string>;
};

const getDayConcurrency = (days: Record<string, DayConcurrency>, dateKey: string) => {
    if (!days[dateKey]) {
        days[dateKey] = {
            bookedPerMinute: new Uint16Array(MINUTES_PER_DAY),
            scheduledPerMinute: new Uint16Array(MINUTES_PER_DAY),
            technicians: new Set<string>(),
        };
    }
    const day = days[dateKey];
    return day;
};

const addEventToConcurrency = (perMinute: Uint16Array, start: Date, end: Date) => {
    const startMinute = start.getHours() * 60 + start.getMinutes();
    const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
    const endMinute = Math.min(MINUTES_PER_DAY, startMinute + durationMinutes);
    for (let minute = startMinute; minute < endMinute; minute++) {
        perMinute[minute] += 1;
    }
};

const buildDailyHours = (dateKey: string, day: DayConcurrency, discountBreaks: boolean) => {
    let rawBookedMinutes = 0;
    let scheduledMinutes = 0;
    let cappedBookedMinutes = 0;

    for (let minute = 0; minute < MINUTES_PER_DAY; minute++) {
        const booked = day.bookedPerMinute[minute];
        const scheduled = day.scheduledPerMinute[minute];
        rawBookedMinutes += booked;
        scheduledMinutes += scheduled;
        cappedBookedMinutes += Math.min(booked, scheduled);
    }

    let discountMinutes = 0;
    if (discountBreaks) {
        const sampleDate = new Date(`${dateKey}T12:00:00`);
        discountMinutes = day.technicians.size * breakHoursForDate(sampleDate) * 60;
    }
    const scheduledHours = Math.max(0, scheduledMinutes - discountMinutes) / 60;

    // Without capacity there is no ratio to protect — show the raw booked hours as plain information
    if (scheduledMinutes === 0) {
        const infoOnlyHours = { bookedHours: rawBookedMinutes / 60, scheduledHours };
        return infoOnlyHours;
    }

    const bookedHours = Math.min(cappedBookedMinutes / 60, scheduledHours);
    const dailyHours = { bookedHours, scheduledHours };
    return dailyHours;
};

// Booked minutes only count while there is free scheduled capacity at that exact moment (1 technician = 1
// session); anything above capacity is bonus work and is ignored, so overbooked periods cannot hide gaps.
export const calculateDailyHoursMap = (
    sessionEvents: Array<any>,
    overlayEvents: Array<OverlayEvent> | undefined,
    discountBreaks: boolean = false,
    visibleRange?: DateRangeBounds
) => {
    const days: Record<string, DayConcurrency> = {};

    sessionEvents.forEach((event) => {
        if (!isWithinRange(event.start, visibleRange)) return;
        const day = getDayConcurrency(days, toDateKey(event.start));
        addEventToConcurrency(day.bookedPerMinute, event.start, event.end);
    });

    (overlayEvents || []).forEach((event) => {
        if (event.overlayType !== "schedule") return;
        if (!isWithinRange(event.start, visibleRange)) return;
        const day = getDayConcurrency(days, toDateKey(event.start));
        addEventToConcurrency(day.scheduledPerMinute, event.start, event.end);
        if (discountBreaks && event.userId) day.technicians.add(event.userId);
    });

    const map: DailyHoursMap = {};
    Object.entries(days).forEach(([dateKey, day]) => {
        map[dateKey] = buildDailyHours(dateKey, day, discountBreaks);
    });

    return map;
};

export const countEventsInRange = (sessionEvents: Array<any>, visibleRange: DateRangeBounds | undefined) => {
    const visibleEvents = sessionEvents.filter((event) => isWithinRange(event.start, visibleRange));
    const count = visibleEvents.length;
    return count;
};

export const formatEventsCountLabel = (eventsCount: number) => {
    const noun = eventsCount === 1 ? "evento" : "eventos";
    const label = `${eventsCount} ${noun}`;
    return label;
};

export const getDayHours = (map: DailyHoursMap, date: Date) => {
    const dateKey = toDateKey(date);
    const hours = map[dateKey] || { bookedHours: 0, scheduledHours: 0 };
    return hours;
};

export const sumHoursInMap = (map: DailyHoursMap) => {
    const totals = Object.values(map).reduce(
        (acc, value) => {
            acc.bookedHours += value.bookedHours;
            acc.scheduledHours += value.scheduledHours;
            return acc;
        },
        { bookedHours: 0, scheduledHours: 0 }
    );
    return totals;
};

export const computeUtilizationColor = (hours: DailyHours) => {
    if (hours.scheduledHours === 0) return "text.secondary";
    const ratio = hours.bookedHours / hours.scheduledHours;
    if (ratio > 0.8) return "success.main";
    if (ratio >= 0.6) return "warning.main";
    const color = "error.main";
    return color;
};

export const formatHoursLabel = (hours: DailyHours) => {
    const booked = Math.round(hours.bookedHours);
    const scheduled = Math.round(hours.scheduledHours);
    if (scheduled === 0) {
        const label = `${booked}h`;
        return label;
    }
    const percentage = Math.round((hours.bookedHours / hours.scheduledHours) * 100);
    const label = `${booked}h/${scheduled}h (${percentage}%)`;
    return label;
};
